<template>
  <div class="app-container">
    <div class="card card-custom mb-5">
      <div class="card-body">
        <!-- Calendar table -->
        <div class="calendar-container">
          <div class="calendar-wrapper">
            <table class="calendar-table">
              <colgroup>
                <col class="week-column" />
                <col
                  v-for="day in days"
                  :key="`col-${day}`"
                  class="day-column"
                />
              </colgroup>
              <thead>
                <tr class="header-row">
                  <th class="week-cell">Tuần</th>
                  <th v-for="day in days" :key="day" class="day-header">
                    {{ day }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="week in numberOfWeeks"
                  :key="week"
                  class="calendar-row"
                >
                  <td class="week-cell">Tuần {{ week }}</td>
                  <td
                    v-for="day in days"
                    :key="`${week}-${day}`"
                    class="day-cell"
                  >
                    <div class="event-container" ref="eventContainer">
                      <div
                        v-for="event in getEventsStartingOnDay(week, day)"
                        :key="event.id"
                        class="event-card"
                        :style="{
                          backgroundColor: event.color || '#F4B183',
                          color: getContrastColor(event.color || '#F4B183'),
                        }"
                      >
                        <div class="event-content">
                          <span class="event-title">{{ event.title }}</span>
                          <div class="event-actions">
                            <button
                              @click="
                                deleteActivity(event.activityModuleMappingId)
                              "
                              class="icon-button delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    moduleId: {
      type: [String, Number],
      default: null,
    },
    moduleData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      days: [
        'Ngày 1',
        'Ngày 2',
        'Ngày 3',
        'Ngày 4',
        'Ngày 5',
        'Ngày 6',
        'Ngày 7',
      ],
      events: [],
      loading: false,
      defaultColor: '#16A45A',
      numberOfWeeks: 0,
    };
  },
  watch: {
    moduleData: {
      handler(newData) {
        if (
          newData &&
          newData.activities &&
          Array.isArray(newData.activities)
        ) {
          this.processActivities(newData.activities);
        }
      },
      immediate: true,
      deep: true,
    },
    moduleId: {
      // handler(newId) {
      //   if (newId) {
      //     this.loadActivitiesByModule(newId);
      //   }
      // },
      // immediate: true,
    },
  },
  methods: {
    getContrastColor(hexColor) {
      hexColor = hexColor.replace('#', '');

      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      return luminance < 0.5 ? '#FFFFFF' : '#000000';
    },

    // Method to get events for a specific week and day
    getEventsStartingOnDay(week, day) {
      const dayIndex = this.days.indexOf(day);
      if (dayIndex === -1) return [];

      const filteredEvents = this.events
        .filter((e) => {
          if (!e.startDay) {
            console.warn(`Event with id ${e.id} has undefined startDay`);
            return false;
          }
          return e.week === week && e.startDay === day;
        })
        .sort((a, b) => a.id.localeCompare(b.id));

      return filteredEvents;
    },

    // Process the activities array into the format needed for the calendar
    processActivities(activities) {
      if (!Array.isArray(activities)) {
        console.error('Activities is not an array:', activities);
        this.events = [];
        return;
      }

      this.events = activities.map((item) => {
        const startAtDay = Number(item.startAtDay);
        if (!startAtDay || startAtDay < 1) {
          console.warn(
            `Invalid startAtDay for item ${item.id}: ${item.startAtDay}`,
          );
          return {
            id: item.id,
            activityId: item.id,
            week: 1,
            startDay: this.days[0], // Mặc định 'Ngày 1'
            title: item.name || 'Không lấy được',
            color: item.color || this.defaultColor,
            activityModuleMappingId: item.activityModuleMappingId || null,
          };
        }

        const week = Math.floor((startAtDay - 1) / 7) + 1;
        const dayOfWeekIndex = (startAtDay - 1) % 7;
        const startDay = this.days[dayOfWeekIndex];

        return {
          id: item.id,
          activityId: item.id,
          week: week,
          startDay: startDay,
          title: item.name || 'Không lấy được',
          color: item.color || this.defaultColor,
          activityModuleMappingId: item.activityModuleMappingId || null,
        };
      });
    },
    // Method to add new activity (can be called from parent)
    addActivity(activityData) {
      // Calculate week based on startAtDay
      const week = Math.floor((activityData.startAtDay - 1) / 7) + 1;

      // Calculate day of week
      const dayOfWeekIndex = (activityData.startAtDay - 1) % 7;
      const startDay = this.days[dayOfWeekIndex];

      const newEvent = {
        id: activityData.id || `temp-${Date.now()}`,
        activityId: activityData.activityId,
        week: week,
        startDay: startDay,
        title: activityData.name,
        color: activityData.color || this.defaultColor,
        activityModuleMappingId: activityData.activityModuleMappingId,
      };

      // Add to events array
      this.events.push(newEvent);
    },

    // Method to update existing activity
    updateActivity(activityData) {
      const index = this.events.findIndex(
        (e) =>
          e.activityModuleMappingId === activityData.activityModuleMappingId,
      );

      if (index !== -1) {
        // Calculate week based on startAtDay
        const week = Math.floor((activityData.startAtDay - 1) / 7) + 1;

        // Calculate day of week
        const dayOfWeekIndex = (activityData.startAtDay - 1) % 7;
        const startDay = this.days[dayOfWeekIndex];

        // Update the event
        this.events[index] = {
          ...this.events[index],
          week: week,
          startDay: startDay,
          title: activityData.name,
          color: activityData.color || this.defaultColor,
        };

        // Force update to trigger reactivity
        this.events = [...this.events];
      }
    },

    // Method to delete activity
    deleteActivity(activityModuleMappingId) {
      this.$swal({
        title: 'Xác nhận',
        text: 'Bạn có chắc chắn muốn xóa hoạt động này?',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Hủy',
            value: false,
            visible: true,
            className: 'btn btn-secondary',
            closeModal: true,
          },
          confirm: {
            text: 'Xóa',
            value: true,
            visible: true,
            className: 'btn btn-danger',
            closeModal: true,
          },
        },
      }).then((result) => {
        if (result) {
          // Filter out the deleted event
          this.events = this.events.filter(
            (e) => e.activityModuleMappingId !== activityModuleMappingId,
          );

          // Emit event for parent component to handle the API call
          this.$emit('delete-activity', activityModuleMappingId);
        }
      });
    },

    // Method to edit an activity
    editActivity(event) {
      this.$emit('edit-activity', event);
    },

    // Method to update number of weeks
    updateNumberOfWeeks(newNumberOfWeeks) {
      this.numberOfWeeks = newNumberOfWeeks;
    },
  },
  mounted() {},
};
</script>

<style scoped>
.app-container {
  padding: 0;
}

.calendar-container {
  width: 100%;
  position: relative;
}

.calendar-wrapper {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scroll-behavior: smooth;
}

.calendar-table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed; /* Important for fixed width columns */
}

/* Define column widths */
.week-column {
  width: 80px;
}

.day-column {
  width: calc((100% - 80px) / 7); /* Evenly distribute remaining space */
}

.header-row th {
  padding: 8px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  white-space: nowrap;
}

.day-header {
  width: calc((100% - 80px) / 7); /* Ensure header has correct width */
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-cell {
  width: 80px;
  text-align: center;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
}

.day-cell {
  border: 1px solid #ddd;
  vertical-align: top;
  height: 80px;
  padding: 4px;
  width: calc((100% - 80px) / 7); /* Match column width */
}

.event-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.event-card {
  padding: 4px 8px;
  font-size: 12px;
  height: auto;
  display: flex;
  align-items: center;
  border-radius: 2px;
  margin-bottom: 5px;
}

.event-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.event-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-weight: 500;
}

.event-actions {
  display: flex;
  gap: 4px;
  margin-left: 8px;
  flex-shrink: 0;
}

.icon-button {
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  opacity: 0.7;
}

.icon-button:hover {
  opacity: 1;
}

/* Responsive styling */
@media (max-width: 992px) {
  .week-column {
    width: 60px;
  }

  .week-cell {
    width: 60px;
  }

  .day-column {
    width: calc((100% - 60px) / 7);
  }

  .day-cell {
    width: calc((100% - 60px) / 7);
    height: 60px;
  }

  .event-card {
    padding: 2px 4px;
    font-size: 10px;
  }

  .event-actions {
    margin-left: 4px;
  }

  .icon-button {
    font-size: 10px;
  }
}
</style>
